import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { getMeRequest } from "Api/me/getMe.request";
import { addSshKeyRequest } from "Api/sshKey/addSshKey.request";
import { deleteSshKeyRequest } from "Api/sshKey/deleteSshKey.request";
import { getSshKeysRequest } from "Api/sshKey/getSshKeys.request";
import {
  addLegacySshKeyRequest,
  deleteLegacySshKeyRequest,
  fromLegacySshKey
} from "Api/sshKey/legacySshKey";
import logger from "Libs/logger";
import { setDeep } from "Libs/objectAccess";
import { hasHtml } from "Libs/utils";
import { selectCurrentUserUUID } from "Reducers/app/selectors";
import { selectSshKeysApiEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { RootState } from "Store/configureStore";

import type { SshKey } from "Api/sshKey/types";

export const getSshKeyErrorStatus = (error: unknown) => {
  if (!error || typeof error !== "object") return undefined;
  const { code, status, statusCode } = error as Record<string, unknown>;
  const value = [code, status, statusCode].find(
    candidate =>
      typeof candidate === "number" ||
      (typeof candidate === "string" && /^\d+$/u.test(candidate))
  );

  return value === undefined ? undefined : Number(value);
};

export const isSshKeyConflictError = (error: unknown) => {
  if (typeof error === "string") {
    try {
      return isSshKeyConflictError(JSON.parse(error));
    } catch {
      const message = error.toLowerCase();
      return (
        message.includes("already registered") ||
        message.includes("is already in use")
      );
    }
  }

  if (!error || typeof error !== "object") return false;

  const { title } = error as Record<string, unknown>;
  const normalizedTitle = typeof title === "string" ? title.toLowerCase() : "";
  return (
    getSshKeyErrorStatus(error) === 409 ||
    normalizedTitle === "conflict" ||
    normalizedTitle.includes("is already in use")
  );
};

export const loadSshKeys = createAsyncThunk(
  "app/users/sshkeys/load",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      if (selectSshKeysApiEnabled(state)) {
        const userId = selectCurrentUserUUID(state);
        const { items } = await getSshKeysRequest(userId);
        return items;
      }

      const me = await getMeRequest();
      return (me.ssh_keys ?? []).map(fromLegacySshKey);
    } catch (err) {
      if (
        ![404, 403, 400].includes(getSshKeyErrorStatus(err) ?? 0) &&
        !hasHtml(err as string)
      ) {
        logger(err, {
          action: "user_add_sshkey"
        });
      }
      return rejectWithValue({ errors: err });
    }
  }
);

type AddSshKeyThunkArgType = {
  sshKey: { value: string; label?: string };
};
export const addSshKey = createAsyncThunk<SshKey, AddSshKeyThunkArgType>(
  "app/users/sshKeys/add",
  async ({ sshKey }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      if (selectSshKeysApiEnabled(state)) {
        const userId = selectCurrentUserUUID(state);
        return await addSshKeyRequest({
          userId,
          value: sshKey.value,
          label: sshKey.label
        });
      }

      return fromLegacySshKey(
        await addLegacySshKeyRequest({
          value: sshKey.value,
          title: sshKey.label || null
        })
      );
    } catch (err: any) {
      // 409 is the new API's "key already registered"; it is surfaced in the UI.
      if (
        ![404, 403, 400, 409].includes(getSshKeyErrorStatus(err) ?? 0) &&
        !hasHtml(err)
      ) {
        logger(err, {
          action: "user_add_sshkey"
        });
      }
      return rejectWithValue({ errors: err });
    }
  }
);

type DeleteSshKeyThunkArgType = {
  id: string;
};
export const deleteSshKey = createAsyncThunk<SshKey, DeleteSshKeyThunkArgType>(
  "app/users/sshKeys/delete",
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const sshKey = state.userSshKey.data[id];

      if (!sshKey) {
        throw Error("Ssh key not found");
      }

      if (selectSshKeysApiEnabled(state)) {
        const userId = selectCurrentUserUUID(state);
        await deleteSshKeyRequest({ userId, keyId: id });
      } else {
        await deleteLegacySshKeyRequest(id);
      }

      return sshKey;
    } catch (err: any) {
      if (
        ![404, 403].includes(getSshKeyErrorStatus(err) ?? 0) &&
        !hasHtml(err)
      ) {
        logger(err, {
          action: "user_delete_sshkey"
        });
      }

      return rejectWithValue({ errors: err });
    }
  }
);

export type SshKeyState = Readonly<{
  data: Record<string, SshKey>;
  loading: boolean;
  status?: "added" | "deleted" | "idle" | "pending" | "rejected" | "updated";
  editedLine?: boolean;
  error?: any;
}>;

const initialState: SshKeyState = { data: {}, loading: false, status: "idle" };

const sshKeys = createSlice({
  name: "sshkeys",
  initialState,
  reducers: {
    editLine(state, action) {
      return { ...state, editedLine: action.payload.index };
    },
    cancelAddSshKey(state) {
      return { ...state, editedLine: false, error: {} };
    }
  },
  extraReducers: builder => {
    builder
      // GET
      .addCase(loadSshKeys.pending, state => {
        delete state.error;
        state.loading = true;
      })
      .addCase(loadSshKeys.fulfilled, (state, action) => {
        state.data = action.payload.reduce(
          (list, key) => {
            if (key.id) {
              setDeep(list, [key.id], key);
            }
            return list;
          },
          {} as Record<string, SshKey>
        );

        state.loading = false;
      })
      .addCase(loadSshKeys.rejected, (state, action) => {
        const { errors } = action.payload as { errors: string };

        state.error = errors;
        state.loading = false;
      })

      // ADD
      .addCase(addSshKey.pending, state => {
        delete state.error;
        state.loading = true;
        state.status = "pending";
      })
      .addCase(addSshKey.fulfilled, (state, action) => {
        setDeep(state, ["data", action.payload.id], action.payload);
        state.editedLine = false;
        state.loading = false;
        state.status = "added";
      })
      .addCase(addSshKey.rejected, (state, action) => {
        const { errors } = action.payload as { errors: string };
        state.error = errors;
        state.loading = false;
        state.status = "rejected";
      })

      // DELETE
      .addCase(deleteSshKey.pending, state => {
        delete state.error;
        state.loading = true;
      })
      .addCase(deleteSshKey.fulfilled, (state, action) => {
        delete state.data?.[action.payload.id];
        state.loading = false;
      })
      .addCase(deleteSshKey.rejected, (state, action) => {
        const { errors } = action.payload as { errors: string };
        state.error = errors;
      });
  }
});

export const { editLine, cancelAddSshKey } = sshKeys.actions;
export default sshKeys.reducer;
